/**
 * Admin Hub Route Component
 * Wraps Admin Hub routes to ensure proper permission checks
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useCurrentUser, type UserInfo } from '../hooks/useApi';
import { hasAnyAdminAccess, getAdminLayoutMode } from '../config/adminHubConfig';

interface AdminHubRouteProps {
  children: React.ReactNode;
  /** If true, allows config-only users (accounting.view, market_compass.customize) */
  allowConfigOnly?: boolean;
}

/**
 * Route wrapper for Admin Hub pages
 * Checks if user has appropriate admin permissions
 */
export const AdminHubRoute: React.FC<AdminHubRouteProps> = ({
  children,
  allowConfigOnly = false,
}) => {
  const { data: currentUser, isLoading } = useCurrentUser() as {
    data: UserInfo | null | undefined;
    isLoading: boolean;
  };

  // Loading state
  if (isLoading || currentUser === undefined) {
    return null;
  }

  // Not authenticated
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const userPermissions = currentUser.permissions ?? [];
  const layoutMode = getAdminLayoutMode(userPermissions);

  // No access at all
  if (layoutMode === 'none') {
    return <Navigate to="/" replace />;
  }

  // Config-only users trying to access full admin pages
  if (layoutMode === 'config-only' && !allowConfigOnly) {
    // Redirect to their appropriate section
    if (userPermissions.includes('accounting.view')) {
      return <Navigate to="/admin/hub/accounting/my-vendors" replace />;
    }
    if (userPermissions.includes('market_compass.customize')) {
      return <Navigate to="/admin/hub/marketcompass/my-markets" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminHubRoute;
